/**
 * Remove named parameters from a query string.
 *
 * Split out of `UrlQueryService` so it can be unit-tested: that service reads `window`,
 * `document.referrer`, `sessionStorage` and `navigator.userAgent` while it is being
 * built, so a test cannot construct one. Same reasoning as `safe-url-for-log.ts` and
 * `breadcrumb-redaction.ts`, which were lifted out for the same reason.
 */

/**
 * The parameter that must survive being cleared, whatever the caller asked for.
 *
 * `breadcrumbs` is the page trail the app restores a deep link from. Dropping it turns a
 * link into "the same page with no way back", so it is put back even if a caller lists
 * it among the keys to remove.
 */
const ALWAYS_KEPT_KEY = 'breadcrumbs';

/**
 * The query string with `keys` taken out of it, `breadcrumbs` always left in.
 *
 * Takes and returns the `location.search` form — leading `?`, or empty when nothing is
 * left — so the caller can assign it straight onto a `URL`.
 */
export function removeUrlSearchKeys(search: string, keys: string[]): string {
  const params = new URLSearchParams(search);
  const kept = params.get(ALWAYS_KEPT_KEY);

  for (const key of keys) {
    params.delete(key);
  }

  // put the page trail back if removing the caller's keys took it away
  if (kept !== null) {
    params.set(ALWAYS_KEPT_KEY, kept);
  }

  const remaining = params.toString();
  return remaining ? `?${remaining}` : '';
}
